/**
 * SixBestFriends - Six journalistic questions with debounced auto-save
 *
 * Key requirements:
 * - Auto-save on BLUR only (not on keystroke) to prevent race conditions
 * - 500ms debounce after blur before persisting
 * - Prevents multiple concurrent API calls that can cause inconsistent saves
 */

import { useState, useCallback, useRef, useEffect } from 'react'

interface SixBestFriendsData {
  who?: string
  what?: string
  when?: string
  where?: string
  why?: string
  how?: string
}

interface SixBestFriendsProps {
  sixBestFriends: SixBestFriendsData
  onUpdate: (field: string, value: string | undefined) => void
}

interface Field {
  key: string
  label: string
  placeholder: string
}

const FIELDS: Field[] = [
  { key: 'who', label: 'Who', placeholder: 'Who is the song about?' },
  { key: 'what', label: 'What', placeholder: 'What happens in the song?' },
  { key: 'when', label: 'When', placeholder: 'When does it take place?' },
  { key: 'where', label: 'Where', placeholder: 'Where does it happen?' },
  { key: 'why', label: 'Why', placeholder: 'Why is this story important?' },
  { key: 'how', label: 'How', placeholder: 'How does the story unfold?' },
]

// Debounce delay for blur saves — 500ms as per CoreQuestions pattern
const BLUR_DEBOUNCE_MS = 500

export function SixBestFriends({ sixBestFriends, onUpdate }: SixBestFriendsProps) {
  // Local state for tracking what user is typing (before save)
  // Initialized from props; gets fresh values when component remounts (modal reopen)
  const [localValues, setLocalValues] = useState<SixBestFriendsData>(sixBestFriends)

  // Keep track of pending debounced saves so we can clean up on unmount
  const pendingTimeoutsRef = useRef<Map<string, NodeJS.Timeout>>(new Map())

  // Clean up any pending timeouts on unmount to prevent state leaks
  useEffect(() => {
    // Capture the current ref value for cleanup (ESLint wants this)
    const timeouts = pendingTimeoutsRef.current
    return () => {
      timeouts.forEach((timeoutId) => clearTimeout(timeoutId))
      timeouts.clear()
    }
  }, [])

  // Handle text changes locally (no save yet)
  const handleChange = useCallback((field: string, value: string) => {
    setLocalValues((prev) => ({
      ...prev,
      [field]: value || undefined,
    }))
  }, [])

  // Save on blur with 500ms debounce — only fires if value actually changed
  const handleBlur = useCallback(
    (field: string) => {
      // Clear any existing pending timeout for this field
      const existingTimeout = pendingTimeoutsRef.current.get(field)
      if (existingTimeout) {
        clearTimeout(existingTimeout)
      }

      // Schedule the debounced save
      const timeoutId = setTimeout(() => {
        // Re-check local value at save time (closure captures fresh ref)
        setLocalValues((currentValues) => {
          const value = currentValues[field as keyof SixBestFriendsData]
          // Only persist if value actually changed from what's in props
          if (value !== sixBestFriends[field as keyof SixBestFriendsData]) {
            onUpdate(field, value)
          }
          return currentValues // No state change, just reading
        })
        pendingTimeoutsRef.current.delete(field)
      }, BLUR_DEBOUNCE_MS)

      pendingTimeoutsRef.current.set(field, timeoutId)
    },
    [sixBestFriends, onUpdate]
  )

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {FIELDS.map(({ key, label, placeholder }) => {
        const value = localValues[key as keyof SixBestFriendsData] || ''

        return (
          <div key={key}>
            <label
              htmlFor={`six-best-friends-${key}`}
              className="block text-sm font-medium text-gray-300 mb-2"
            >
              {label}
            </label>
            <input
              id={`six-best-friends-${key}`}
              data-testid={`six-best-friends-${key}`}
              type="text"
              value={value}
              onChange={(e) => handleChange(key, e.target.value)}
              onBlur={() => handleBlur(key)}
              placeholder={placeholder}
              className="w-full border border-white/20 rounded-md px-3 py-2 text-sm text-white bg-white/10 backdrop-blur-sm placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-400"
            />
          </div>
        )
      })}
    </div>
  )
}
